import { signal } from '@preact/signals';
import { useEffect, useState, useRef } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { api, ApiError } from '../api/client';
import { TenantLogo } from '../components/TenantLogo';
import { tenantBranding, tenantLoaded } from '../stores/tenant.store';
import { T } from '../styles/tokens';
import { Button, Divider, Input } from '../components/ui';
import { TwoFactorVerify } from '../components/TwoFactorVerify';

const email = signal('');
const password = signal('');
const loading = signal(false);
const sent = signal(false);
const error = signal('');
const showPassword = signal(false);

// Config de auth carregada da API
const authConfig = signal<Record<string, string> | null>(null);
const googleLoading = signal(false);

declare global {
  interface Window { turnstile: any; google: any; }
}

async function handleSubmit(e: Event) {
  e.preventDefault();
  error.value = '';
  loading.value = true;
  try {
    const turnstileToken = (document.querySelector('[name="cf-turnstile-response"]') as HTMLInputElement)?.value ?? 'dev-token';
    await api.post('/auth/magic-link', { email: email.value, turnstileToken });
    sent.value = true;
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : i18n.t('auth:error_send_link');
  } finally {
    loading.value = false;
  }
}

// Signal para 2FA
const needs2FA = signal(false);
const tempToken2FA = signal('');

async function handlePasswordLogin(e: Event) {
  e.preventDefault();
  error.value = '';
  loading.value = true;
  try {
    const turnstileToken = (document.querySelector('[name="cf-turnstile-response"]') as HTMLInputElement)?.value ?? 'dev-token';
    const res = await api.post<any>('/auth/login-password', { email: email.value, password: password.value, turnstileToken });
    if (res?.requires_2fa && res?.tempToken) {
      tempToken2FA.value = res.tempToken;
      needs2FA.value = true;
      return;
    }
    window.location.href = '/chat';
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : i18n.t('auth:error_wrong_credentials');
  } finally {
    loading.value = false;
  }
}

// Estado de envio do magic link (tela de confirmacao)
function SentState() {
  const { t } = useTranslation('auth');
  return (
    <div style={{ textAlign: 'center' }}>
      {/* Icone de envio */}
      <div style={{
        width: 64, height: 64, borderRadius: '50%', margin: '0 auto 24px',
        background: `${T.primaryBg}`,
        border: `1px solid ${T.primaryBgStrong}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" />
        </svg>
      </div>

      <h2 style={{ fontSize: T.text2xl, fontWeight: T.weightBold, color: T.text, margin: '0 0 8px' }}>
        {t('email_sent')}
      </h2>
      <p style={{ color: T.textSecondary, fontSize: T.textMd, marginBottom: 20, lineHeight: 1.5 }}>
        {t('magic_link_sent')}
      </p>

      {/* Badge do email */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`,
        borderRadius: T.radiusMd, padding: '10px 20px', marginBottom: 24,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 4L12 13L2 4" />
        </svg>
        <span style={{ color: T.primary, fontSize: T.textMd, fontWeight: T.weightSemibold, wordBreak: 'break-all' }}>
          {email.value}
        </span>
      </div>

      {/* Info cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div style={{
          flex: 1, background: T.bgCard, borderRadius: T.radiusMd,
          padding: '14px 12px', border: `1px solid ${T.border}`,
        }}>
          <div style={{ fontSize: T.textXs, color: T.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: T.weightSemibold }}>{t('expires_in')}</div>
          <div style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text }}>{t('minutes')}</div>
        </div>
        <div style={{
          flex: 1, background: T.bgCard, borderRadius: T.radiusMd,
          padding: '14px 12px', border: `1px solid ${T.border}`,
        }}>
          <div style={{ fontSize: T.textXs, color: T.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: T.weightSemibold }}>{t('not_received')}</div>
          <div style={{ fontSize: T.textSm, fontWeight: T.weightMedium, color: T.textSecondary }}>{t('check_spam')}</div>
        </div>
      </div>

      <Button variant="ghost" onClick={() => { sent.value = false; }}>
        {t('try_another_email')}
      </Button>
    </div>
  );
}

// Callback do Google Sign-In
async function handleGoogleCredential(response: any) {
  error.value = '';
  loading.value = true;
  googleLoading.value = true;
  try {
    await api.post('/auth/google', { credential: response.credential });
    window.location.href = '/chat';
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : i18n.t('auth:error_google_login');
    googleLoading.value = false;
  } finally {
    loading.value = false;
  }
}

export function LoginPage() {
  // Se precisa de 2FA, mostra tela de verificação
  if (needs2FA.value) {
    return <TwoFactorVerify tempToken={tempToken2FA.value} />;
  }

  const { t } = useTranslation('auth');
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Resetar loading quando janela ganha foco (popup Google fechou sem login)
  useEffect(() => {
    const onFocus = () => {
      if (googleLoading.value && !loading.value) {
        setTimeout(() => { if (googleLoading.value && !loading.value) googleLoading.value = false; }, 1000);
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Carregar config de auth da API
  useEffect(() => {
    api.get<any>('/auth/config').then((res: any) => {
      // api.get retorna data.data unwrapped
      authConfig.value = res || {};
      setConfigLoaded(true);
    }).catch(() => {
      setConfigLoaded(true);
    });
  }, []);

  // Carregar GIS e renderizar botão Google
  useEffect(() => {
    if (!configLoaded || authConfig.value?.google_enabled !== 'true' || !authConfig.value?.google_client_id) return;

    const clientId = authConfig.value.google_client_id;

    // Carregar script GIS
    if (!document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => initGoogle(clientId);
      document.head.appendChild(script);
    } else if (window.google?.accounts) {
      initGoogle(clientId);
    }

    function initGoogle(cid: string) {
      if (!window.google?.accounts) return;
      // Inicializar GIS com callback
      window.google.accounts.id.initialize({
        client_id: cid,
        callback: handleGoogleCredential,
        auto_select: false,
        cancel_on_tap_outside: true,
        intermediate_iframe_close_callback: () => { googleLoading.value = false; },
      });
      // Renderizar botão GIS real (transparente, recebe clique real do usuário)
      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard', size: 'large', text: 'continue_with', width: 400, theme: 'filled_black',
          click_listener: () => { googleLoading.value = true; },
        });
      }
      // One Tap automático (se habilitado no admin)
      if (authConfig.value?.google_one_tap_enabled === 'true') {
        window.google.accounts.id.prompt();
      }
    }
  }, [configLoaded]);

  const cfg = authConfig.value;
  const googleEnabled = cfg?.google_enabled === 'true' && !!cfg?.google_client_id;
  const magicLinkEnabled = cfg?.magic_link_enabled !== 'false'; // default true
  const passwordEnabled = cfg?.password_login_enabled !== 'false'; // default true
  const turnstileSiteKey = cfg?.turnstile_site_key || '1x00000000000000000000AA';

  // Aguarda branding carregar antes de renderizar (evita piscar)
  if (!tenantLoaded.value) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg }} />
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '40px 20px',
      background: `radial-gradient(ellipse at 50% 0%, ${T.primaryBgSubtle} 0%, transparent 60%), ${T.bg}`,
      fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Logo + Titulo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <TenantLogo size={72} style={{ borderRadius: 16, marginBottom: 16 }} />
          <h1 style={{
            fontSize: T.text3xl, fontWeight: T.weightBold, color: T.text,
            margin: '0 0 6px', letterSpacing: '-0.5px',
          }}>
            {t('login_title', { appName: tenantBranding.value?.name || '' })}
          </h1>
          <p style={{ fontSize: T.textMd, color: T.textSecondary, margin: 0 }}>
            {t('login_subtitle')}
          </p>
        </div>

        {/* Card principal */}
        <div style={{
          background: T.bgCard,
          border: `1px solid ${T.border}`,
          borderRadius: T.radiusLg,
          padding: 32,
        }}>
          {sent.value ? (
            <SentState />
          ) : (
            <>
              {error.value && (
                <div style={{
                  background: T.dangerBg, border: `1px solid ${T.dangerBgStrong}`,
                  borderRadius: T.radiusMd, padding: '10px 14px', marginBottom: 16,
                  fontSize: T.textSm, color: T.danger,
                }}>
                  {error.value}
                </div>
              )}

              {/* Botão Google Sign-In — wrapper com nosso design + botão GIS real por baixo */}
              {googleEnabled && (
                <>
                  <div data-google-btn style={{ position: 'relative', width: '100%', height: 44, marginBottom: 4 }}>
                    {/* Nosso visual (não-interativo, só visual) */}
                    <div style={{
                      position: 'absolute', inset: 0, borderRadius: T.radiusMd,
                      border: `1px solid ${T.border}`, background: T.bgCard,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                      pointerEvents: 'none', zIndex: 1,
                    }}>
                      {googleLoading.value ? (
                        <>
                          <svg width="18" height="18" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round" />
                          </svg>
                          <span style={{ fontSize: T.textMd, fontWeight: T.weightSemibold, color: T.textSecondary }}>{t('common:loading')}</span>
                        </>
                      ) : (
                        <>
                          <svg width="18" height="18" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 001 12c0 1.94.46 3.77 1.18 5.43l3.66-2.84z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                          <span style={{ fontSize: T.textMd, fontWeight: T.weightSemibold, color: T.text }}>{t('continue_with_google')}</span>
                        </>
                      )}
                    </div>
                    {/* Botão GIS real (transparente, recebe clique real — abre popup) */}
                    <div ref={googleBtnRef} style={{
                      position: 'absolute', inset: 0, zIndex: 2, opacity: 0.01, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }} />
                  </div>
                  {(magicLinkEnabled || passwordEnabled) && <Divider label="ou" spacing={16} />}
                </>
              )}

              {/* Formulario magic link */}
              {magicLinkEnabled && <form onSubmit={handleSubmit}>
                <Input
                  label={t('email_label')}
                  type="email"
                  placeholder={t('email_placeholder')}
                  value={email.value}
                  onChange={(v) => (email.value = v)}
                />
                <div class="cf-turnstile" data-sitekey={turnstileSiteKey} data-theme="dark" style={{ marginTop: 12 }} />
                <Button
                  variant="primary"
                  size="lg"
                  type="submit"
                  disabled={loading.value}
                  style={{ width: '100%', marginTop: 16, justifyContent: 'center' }}
                >
                  {loading.value ? t('sending') : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="none">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                      {t('enter_magic_link')}
                    </>
                  )}
                </Button>
              </form>}

              {/* Divisor + login com senha */}
              {passwordEnabled && magicLinkEnabled && <Divider label="ou" spacing={20} />}

              {/* Expansivel: login com senha */}
              {passwordEnabled && !showPassword.value ? (
                <Button variant="ghost"
                  onClick={() => { showPassword.value = true; error.value = ''; }}
                  style={{
                    width: '100%', padding: '10px 0', background: 'none',
                    border: 'none', color: T.textMuted, fontSize: T.textSm,
                    cursor: 'pointer', transition: `color ${T.transitionFast}`,
                    fontWeight: T.weightMedium,
                  }}
                  onMouseEnter={(e: any) => { e.target.style.color = T.text; }}
                  onMouseLeave={(e: any) => { e.target.style.color = T.textMuted; }}
                >
                  {t('enter_with_password')}
                </Button>
              ) : passwordEnabled ? (
                <form onSubmit={handlePasswordLogin}>
                  <Input
                    label={t('password_label')}
                    type="password"
                    placeholder={t('password_placeholder')}
                    value={password.value}
                    onChange={(v) => (password.value = v)}
                  />
                  <div class="cf-turnstile" data-sitekey={turnstileSiteKey} data-theme="dark" style={{ marginTop: 12 }} />
                  <Button
                    variant="primary"
                    size="lg"
                    type="submit"
                    disabled={loading.value}
                    style={{ width: '100%', marginTop: 16, justifyContent: 'center' }}
                  >
                    {loading.value ? t('entering') : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                        {t('enter')}
                      </>
                    )}
                  </Button>
                  <Button variant="ghost" type="button" onClick={() => { showPassword.value = false; error.value = ''; }} style={{ width: '100%', marginTop: 8 }}>
                    {t('back_to_magic_link')}
                  </Button>
                </form>
              ) : null}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 16,
          marginTop: 24, fontSize: T.textSm, color: T.textMuted,
        }}>
          <a href="/terms" style={{ color: T.textMuted, textDecoration: 'none', transition: `color ${T.transitionFast}` }}
            onMouseEnter={(e: any) => { e.target.style.color = T.textSecondary; }}
            onMouseLeave={(e: any) => { e.target.style.color = T.textMuted; }}>
            {t('common:terms')}
          </a>
          <span style={{ color: T.border }}>|</span>
          <a href="/privacy" style={{ color: T.textMuted, textDecoration: 'none', transition: `color ${T.transitionFast}` }}
            onMouseEnter={(e: any) => { e.target.style.color = T.textSecondary; }}
            onMouseLeave={(e: any) => { e.target.style.color = T.textMuted; }}>
            {t('common:privacy')}
          </a>
        </div>
      </div>
    </div>
  );
}
